import { useMemo } from 'react';

import { useBalanceStore } from '@/entities/balance';
import { useProducts } from '@/entities/pricing/model/useProducts';
import { useSpace } from '@/entities/teamspace';
import { useCurrentUser } from '@/entities/user/currentUser';

import { BoosterType, SubscriptionStatus, UserBoosters } from '@/shared/types/Billing';

import { formatCurrency } from '../lib/formatBoosterPrice';
import { AllBoosters, ScheduledBoosterChanges } from '../lib/types';

type UseBoostersReturn = {
  allBoosters: AllBoosters | null;
  canPurchaseBoosters: boolean;
  hasPurchasedBoosters: boolean;
  hasScheduledBoosterChanges: boolean;
  purchasedBoosters: null | UserBoosters;
  scheduledBoosterChanges: null | ScheduledBoosterChanges;
};

const EMPTY_BOOSTERS: UseBoostersReturn = {
  allBoosters: null,
  canPurchaseBoosters: false,
  hasPurchasedBoosters: false,
  hasScheduledBoosterChanges: false,
  purchasedBoosters: null,
  scheduledBoosterChanges: null,
};

export const useBoosters = (): UseBoostersReturn => {
  const { data: user } = useCurrentUser();
  const { data: space } = useSpace();
  const { products } = useProducts(true);
  const { hasActiveSubscription, hasTrialPlan, purchasedBoosters: spacePurchasedBoosters } = useBalanceStore();


  const isSpaceOwner = useMemo(
    () => !space || space.members?.find((member) => member.email === user?.email)?.role === 'owner',
    [space, user?.email],
  );

  const boostersMap = useMemo(() => {
    if (!products?.boosters || !user?.subscription) return {};

    return products.boosters.reduce(
      (acc, booster) => {
        if (booster.metadata.booster_type) {
          const price = booster.prices[0].unit_amount;
          const { currency } = booster.prices[0];
          const formattedPrice = formatCurrency(price, currency);
          const type = booster.metadata.booster_type as BoosterType;

          if (type) {
            acc[type] = {
              ...booster,
              formattedPrice,
              price,
            };
          }
        }

        return acc;
      },
      {} as AllBoosters,
    );
  }, [products?.boosters, user?.subscription]);

  const scheduledBoosterChanges = useMemo<null | ScheduledBoosterChanges>(() => {
    const scheduledBoosters = user?.subscription?.schedule_info?.boosters;
    if (!scheduledBoosters?.length) return null;

    const result: ScheduledBoosterChanges = {};

    for (const scheduled of scheduledBoosters) {
      for (const [type, booster] of Object.entries(boostersMap) as [BoosterType, AllBoosters[BoosterType]][]) {
        if (booster?.id === scheduled.current_product_id) {
          result[type] = scheduled;
        }
      }
    }

    return Object.keys(result).length > 0 ? result : null;
  }, [user?.subscription?.schedule_info?.boosters, boostersMap]);

  return useMemo(() => {

    if (!user || !products || !user?.subscription) {
      return EMPTY_BOOSTERS;
    }
    
    if (!isSpaceOwner) {
      return {
        allBoosters: boostersMap,
        canPurchaseBoosters: false,
        hasPurchasedBoosters: !!Object.keys(spacePurchasedBoosters ?? {}).length,
        hasScheduledBoosterChanges: false,
        purchasedBoosters: spacePurchasedBoosters,
        scheduledBoosterChanges: null
      };
    }

    return {
      allBoosters: boostersMap,
      canPurchaseBoosters:
        !!Object.keys(boostersMap).length &&
        hasActiveSubscription &&
        !hasTrialPlan &&
        user.subscription.status === SubscriptionStatus.Active,
      hasPurchasedBoosters: !!Object.keys(user.subscription.boosters ?? {}).length,
      hasScheduledBoosterChanges: Boolean(user.subscription.schedule_info?.boosters?.length),
      purchasedBoosters: user.subscription.boosters ?? null,
      scheduledBoosterChanges,
    };
  }, [user, products, boostersMap, hasActiveSubscription, hasTrialPlan, scheduledBoosterChanges, isSpaceOwner, spacePurchasedBoosters]);
};
